import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output, Renderer2, inject } from '@angular/core';
import { Subject, Subscription, catchError, finalize, map, of, startWith, switchMap, takeUntil } from 'rxjs';
import isEmpty from 'lodash-es/isEmpty';
import { AppFacadeService } from '../services/app-facade.service';
import { ConsumerPortalConfig } from '../models/consumer-portal-config';
import { Store, select } from '@ngrx/store';
import { ContractService } from '../services/contract.service';
import { TranslateService } from '@ngx-translate/core';
import { LoggerService } from '../services/logger.service';
import { AppMediatorService } from '../services/app-mediator.service';
import { selectQuickLinkData } from '../store/domains/quicklink/quicklink.selectors';
import { getSelected } from '../store/domains/contract/contract.selectors';
import { getCustomer } from '../store/domains/customer/customer.selectors';
import { Customer } from '../models/consumer';

@Component({
    standalone: false,
    selector: 'por-quick-link',
    templateUrl: './quick-link.component.html',
    styleUrls: ['./quick-link.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class QuickLinkComponent implements OnInit, OnDestroy {
    private readonly store = inject(Store);
    readonly appFacadeService = inject(AppFacadeService);
    private readonly contractService = inject(ContractService);
    private readonly translateService = inject(TranslateService);
    private readonly logger = inject(LoggerService);
    private readonly appMediatorService = inject(AppMediatorService);
    private readonly renderer = inject(Renderer2);

    @Input() config!: string;
    @Output() readonly logout = new EventEmitter();
    @Output() readonly returnToLoginFromQuickLink = new EventEmitter<string>();
    @Output() readonly returnQuickLinkFromLogin = new EventEmitter<string>();
    inputs: ConsumerPortalConfig | undefined;
    subscriptions: Subscription[] = [];
    customerId? = '';
    contractId = '';
    quickLinkUrl = '';
    isLoginButtonVisible = true;
    /**
     * This has been used to complete observable stream when the onDestroy$ subject
     * emits a value (i.e., when the component is destroyed).
     * This will automatically unsubscribe from the observable,
     * preventing any further emissions and ensuring that resources are properly released.
     */
    private readonly onDestroy$: Subject<void> = new Subject<void>();

    quickLinkData$ = this.store.pipe(select(selectQuickLinkData));

    isDetailPageOpened = false;
    isStockType = false;
    invalidRequest = false;
    customer$ = this.store.pipe(
        select(getCustomer),
        map((customer: Customer) => {
            this.appFacadeService.setLoading(true);
            if (isEmpty(customer) && this.customerId) {
                this.appFacadeService.fetchCustomerById(this.customerId);
            }
            return customer;
        }),
        takeUntil(this.onDestroy$),
        finalize(() => {
            this.appFacadeService.setLoading(false);
        })
    );
    // ADO-104813 Issue #2: driven by a trigger so QL can re-fetch onto the new reservation id after an
    // in-app quote→reservation conversion (openContract(newId) below feeds getSelected — see ngOnInit),
    // not just on initial load. catchError/finalize live inside the switchMap so a failed fetch does not
    // tear down the trigger stream and block later reloads.
    private readonly reloadContractDetail$ = new Subject<void>();
    contractDetail$ = this.reloadContractDetail$.pipe(
        startWith(undefined),
        switchMap(() => {
            this.appFacadeService.setLoading(true);
            if (!this.customerId || !this.contractId) {
                this.invalidRequest = true;
                this.appFacadeService.setLoading(false);
                return of([]);
            }
            return this.contractService.getContractsDetails(this.customerId as string, this.contractId as string).pipe(
                switchMap(data => {
                    this.isDetailPageOpened = true;
                    // Add contract to store for payment page. depotId is importat for payment.
                    this.appFacadeService.setContracts([this.contractService.convertContractDetailToContractCP(data[0])]);
                    this.appFacadeService.setContractDetail(data[0]);
                    // ADO-112509: the API may resolve a stale QuickLink id to the original contract, so the
                    // response can carry a different Id than we requested. Key the store off the served id (and
                    // adopt it locally) so selectContractDetail's `Id === selectedContractId` gate matches and the
                    // downstream line-items/history/convert calls use the id GAPI actually recognises. Updating
                    // this.contractId in step keeps the getSelected self-heal guard from re-firing a reload.
                    this.contractId = data[0].Id;
                    this.appFacadeService.openContract(data[0].Id);
                    return of(data);
                }),
                catchError(() => {
                    this.invalidRequest = true;
                    return of([]);
                }),
                finalize(() => {
                    this.appFacadeService.setLoading(false);
                })
            );
        })
    );

    ngOnInit(): void {
        this.quickLinkUrl = window.location.href;
        const lang = this.appMediatorService.localStorageService.selectedContentLanguage;
        if (lang && lang !== this.translateService.getCurrentLang()) {
            this.translateService.use(lang);
        }
        this.injectCustomCss();

        if (this.config && !isEmpty(this.config)) {
            this.inputs = JSON.parse(this.config);
        }
        this.customerId = this.inputs?.customerId ?? '';
        this.contractId = this.inputs?.updatedContractId ?? this.inputs?.contractId ?? '';

        // ADO-104813 Issue #2: the convert effect calls openContract(newReservationId) on success; react to
        // that so QL re-fetches and re-renders onto the new reservation without a full page reload. Guarding
        // on `!== this.contractId` skips the initial/self-triggered emissions and avoids a re-fetch loop.
        this.subscriptions.push(
            this.store.pipe(select(getSelected)).subscribe(selectedContractId => {
                if (selectedContractId && selectedContractId !== this.contractId) {
                    this.contractId = selectedContractId;
                    this.reloadContractDetail$.next();
                }
            })
        );

        this.subscriptions.push(
            this.quickLinkData$.subscribe(quickLinkData => {
                if (!quickLinkData || (!quickLinkData.Id && !quickLinkData.ContractId)) {
                    this.invalidRequest = true;
                }
                if (quickLinkData && quickLinkData.Type === 'Stock') {
                    this.isStockType = true;
                    const stockId: string = quickLinkData?.Id ? quickLinkData?.Id : '0';
                    const orgType = this.inputs?.organizationType ?? quickLinkData?.OrganizationType ?? '';
                    const orgId = this.inputs?.organizationId ?? quickLinkData?.OrganizationId ?? '';
                    this.appFacadeService.loadProductDetail(
                        this.customerId ?? '',
                        this.contractId ?? '',
                        '0',
                        stockId ?? '0',
                        ['Id', 'Name', 'SerialNumber'],
                        () => {
                            this.isDetailPageOpened = true;
                            this.contractId = quickLinkData?.ContractId ?? '';
                            this.customerId = '0'; // customerId is set to '0' to keep API /getpdf endpoint middleware happy
                        },
                        stockId,
                        orgType,
                        orgId
                    );
                }
            })
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.map((sub: Subscription) => sub.unsubscribe());
    }

    logoutUser() {
        this.onDestroy$.next();
        this.onDestroy$.complete();
        this.logout.emit();
        this.returnQuickLinkFromLogin.emit(this.quickLinkUrl);
    }

    returnToLogin() {
        this.returnToLoginFromQuickLink.emit(this.translateService.instant('ViewPdfLogin'));
        this.returnQuickLinkFromLogin.emit(this.quickLinkUrl);
    }

    loginButtonVisibilityChange(isVisible: boolean) {
        this.isLoginButtonVisible = isVisible;
    }

    /**
     * Extracts CSS from URL parameter, decodes it, sanitizes it, and injects it into the page
     */
    private injectCustomCss(): void {
        try {
            const urlParams = new URLSearchParams(window.location.search);
            const encodedCss = urlParams.get('css');

            if (encodedCss) {
                // Check to prevent DoS via extremely large CSS
                const maxCssLength = 10000; // 10KB limit
                if (encodedCss.length > maxCssLength) {
                    this.logger.logError('CSS injection blocked: exceeds maximum allowed length');
                    return;
                }

                // Base64 decode the CSS
                const decodedCss = atob(encodedCss);

                // Sanitize the CSS before injection
                const sanitizedCss = this.sanitizeCss(decodedCss);

                if (sanitizedCss) {
                    // Create and inject style element
                    const styleElement = this.renderer.createElement('style');
                    const styleText = this.renderer.createText(sanitizedCss);
                    this.renderer.appendChild(styleElement, styleText);
                    this.renderer.appendChild(document.head, styleElement);

                    this.logger.logInfo('Custom CSS injected successfully');
                } else {
                    this.logger.logError('CSS injection blocked: potentially malicious content detected');
                }
            }
        } catch (error) {
            this.logger.logError(error);
        }
    }

    /**
     * Sanitizes CSS to prevent injection attacks
     * Blocks dangerous properties, values, and patterns
     */
    private sanitizeCss(css: string): string | null {
        if (!css || typeof css !== 'string') {
            return null;
        }

        // Block CSS exceeding maximum allowed length (10000 characters)
        const maxCssLength = 10000;
        if (css.length > maxCssLength) {
            this.logger.logInfo(`Blocked CSS exceeding max length: ${css.length} > ${maxCssLength}`);
            return null;
        }

        // Dangerous patterns to block
        const dangerousPatterns = [
            /javascript:/gi, // JavaScript protocol
            /expression\s*\(/gi, // IE expression()
            /@import/gi, // @import can load external resources
            /behavior\s*:/gi, // IE behavior property
            /<script/gi, // Script tags
            /on\w+\s*=/gi, // Event handlers (onclick, onload, etc)
            /vbscript:/gi, // VBScript protocol
            /data:text\/html/gi, // Data URI with HTML
            /\.\.\/|\.\.%2f/gi, // Path traversal
            /url\s*\(\s*["']?\s*data:/gi // Data URIs in url() - can be used for exfiltration
        ];

        // Check for dangerous patterns
        for (const pattern of dangerousPatterns) {
            if (pattern.test(css)) {
                this.logger.logInfo(`Blocked CSS containing dangerous pattern: ${pattern.source}`);
                return null;
            }
        }

        // Dangerous/risky CSS properties to block
        const blockedProperties = [
            'behavior', // IE specific, can execute scripts
            'binding', // Mozilla XBL bindings
            '-moz-binding', // Mozilla XBL bindings
            'expression', // IE expressions
            'content', // Can be used to modify UI maliciously
            'cursor' // Can be used with url() for tracking
        ];

        // Parse and validate CSS rules
        const lines = css.split(/[;\n]/);
        const sanitizedLines: string[] = [];

        for (let line of lines) {
            line = line.trim();
            if (!line) continue;

            // Extract property name (handle cases with or without selectors/braces)
            const propertyMatch = line.match(/([a-zA-Z-]+)\s*:/);
            if (propertyMatch) {
                const property = propertyMatch[1].toLowerCase();

                // Check if property is blocked
                if (blockedProperties.includes(property)) {
                    this.logger.logInfo(`Blocked dangerous CSS property: ${property}`);
                    continue;
                }
            }

            // Check for url() values - block external URLs
            if (line.includes('url(')) {
                const urlMatch = line.match(/url\s*\(\s*["']?([^"')]+)["']?\s*\)/i);
                if (urlMatch) {
                    const url = urlMatch[1].trim();
                    // Block absolute URLs (potential exfiltration), allow only relative paths
                    if (url.match(/^https?:\/\//i) || url.match(/^\/\//i) || url.startsWith('data:')) {
                        this.logger.logInfo(`Blocked CSS with external URL: ${url}`);
                        continue;
                    }
                }
            }

            sanitizedLines.push(line);
        }

        const sanitizedCss = sanitizedLines.join(';\n');

        return sanitizedCss;
    }
}
